// src/components/logistique/parametrage/ReglagesInventaires.jsx
// ============================================================================
// Paramétrage de la logistique - les inventaires du parc
// ============================================================================
//
// Le rythme, la photo à un inventaire sur N et la durée d'un inventaire. Ils
// vivaient dans l'onglet Inventaires du module ; ils rejoignent le
// paramétrage (décision de Daniel du 03/11/2026), sans changer de règle ni
// d'enregistrement (InventaireService, routes /inventaires).
//
// Régime B de PARAMETRAGE-MODULES.md : les trois se tiennent (brouillon puis
// « Enregistrer »). Champs texte, jamais numériques (la molette d'une souris y
// retire 1 en silence). Écrire demande LOGISTIQUE.PARC.MANAGE.
// ============================================================================

import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, ClipboardCheck, RotateCcw, Save } from 'lucide-react';
import PorteurNotification from '../../ui/PorteurNotification';
import EnTeteSection from '../../parametrage/EnTeteSection';
import CarteReglages from '../../parametrage/CarteReglages';
import ChargementSection from '../../parametrage/ChargementSection';
import { inventaires } from '../apiLogistique';
import { BandeauEchec } from '../elementsLogistique';

const ACCENT = '#ae7467';

const REGLAGES = [
  {
    cle: 'inventaire_rythme_jours', libelle: 'Rythme des inventaires', unite: 'jours',
    description: "Un inventaire est dû ce nombre de jours après l'ouverture du précédent. Au-delà, une alerte critique le rappelle."
  },
  {
    cle: 'inventaire_photo_tous_les', libelle: 'Photo à un inventaire sur', unite: '',
    description: "1 = à chaque inventaire, 4 = un sur quatre, 0 = jamais. Ces inventaires-là se font au téléphone : chaque actif lu est photographié."
  },
  {
    cle: 'inventaire_duree_jours', libelle: "Durée d'un inventaire", unite: 'jours',
    description: "L'échéance d'un inventaire ouvert. Dépassée sans clôture, une alerte critique le rappelle."
  }
];

const enTexte = (reglages) => Object.fromEntries(Object.entries(reglages).map(([k, v]) => [k, String(v)]));

const ReglagesInventaires = () => {
  const [donnees, setDonnees] = useState(null);
  const [brouillon, setBrouillon] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [enregistrement, setEnregistrement] = useState(false);
  const [toast, setToast] = useState(null);

  const charger = useCallback(async () => {
    try {
      const d = await inventaires.etat();
      setDonnees(d);
      setBrouillon(enTexte(d.reglages));
      setErreur(null);
    } catch (e) {
      setErreur(e.message);
    }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  if (erreur && !donnees) {
    return (
      <BandeauEchec onReessayer={charger}>{erreur}</BandeauEchec>
    );
  }
  if (!donnees || !brouillon) return <ChargementSection accent={ACCENT} libelle="Chargement des réglages des inventaires…" />;
  const gerer = Boolean(donnees.droits?.gerer);
  const modifie = REGLAGES.some((r) => String(donnees.reglages[r.cle]) !== String(brouillon[r.cle]));

  const enregistrer = async () => {
    for (const r of REGLAGES) {
      if (!/^\d+$/.test(String(brouillon[r.cle]).trim())) {
        setToast({ message: `« ${r.libelle} » : un nombre entier.`, type: 'error' });
        return;
      }
    }
    setEnregistrement(true);
    try {
      const reglages = await inventaires.reglages(Object.fromEntries(REGLAGES.map((r) => [r.cle, Number(brouillon[r.cle])])));
      setDonnees((d) => ({ ...d, reglages }));
      setBrouillon(enTexte(reglages));
      setToast({ message: 'Réglages des inventaires enregistrés.', type: 'success' });
    } catch (e) {
      setToast({ message: e.message, type: 'error' });
    } finally {
      setEnregistrement(false);
    }
  };

  return (
    <div className="space-y-5">
      <PorteurNotification notification={toast} onFermer={() => setToast(null)} />
      <EnTeteSection
        icone={ClipboardCheck}
        titre="Les inventaires du parc"
        sousTitre={gerer ? 'Quand un inventaire est dû, combien de temps il dure, et lesquels se font avec photo' : 'Lecture seule : il faut la permission de gérer le parc pour changer ces réglages'}
        accent={ACCENT}
        accentSombre="#8f5d52"
        actions={gerer ? (
          <>
            <button
              type="button"
              onClick={() => setBrouillon(enTexte(donnees.reglages))}
              disabled={!modifie || enregistrement}
              className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3.5 py-2 text-xs font-semibold text-gray-500 transition-all hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <RotateCcw size={13} /> Réinitialiser
            </button>
            <button
              type="button"
              onClick={enregistrer}
              disabled={!modifie || enregistrement}
              className={`flex items-center gap-1.5 rounded-lg border border-transparent px-4 py-2 text-xs font-semibold text-white shadow-sm transition-all ${modifie ? 'bg-[#ae7467] hover:shadow-md' : 'cursor-not-allowed bg-gray-300 shadow-none'}`}
            >
              {enregistrement ? <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" /> : <Save size={13} />}
              Enregistrer
            </button>
          </>
        ) : null}
      />
      <CarteReglages titre="Rythme, photo et durée" accent={ACCENT} compteur="3 réglages" dense>
        {REGLAGES.map((r) => (
          <div key={r.cle} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 hover:bg-gray-50/70">
            <div className="flex min-w-[220px] flex-1 items-center gap-3">
              <div className="h-1.5 w-1.5 flex-shrink-0 rounded-full" style={{ backgroundColor: ACCENT }} />
              <div>
                <label htmlFor={r.cle} className="text-sm font-semibold text-gray-800">{r.libelle}</label>
                <p className="mt-0.5 text-[11px] leading-relaxed text-gray-400">{r.description}</p>
              </div>
            </div>
            <div className="relative w-36">
              <input
                id={r.cle}
                type="text"
                inputMode="numeric"
                autoComplete="off"
                disabled={!gerer}
                className={`h-9 w-full rounded-lg border border-gray-200 bg-white px-3 text-right text-sm text-gray-800 outline-none transition focus:border-[#ae7467] focus:ring-2 focus:ring-[#ae7467]/15 disabled:bg-gray-50 ${r.unite ? 'pr-14' : ''}`}
                value={brouillon[r.cle]}
                onChange={(e) => setBrouillon((b) => ({ ...b, [r.cle]: e.target.value.replace(/[^\d]/g, '') }))}
              />
              {r.unite && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">{r.unite}</span>}
            </div>
          </div>
        ))}
      </CarteReglages>
      {modifie && (
        <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-amber-100"><AlertCircle size={15} className="text-amber-600" /></div>
          <p className="text-xs font-semibold text-amber-700">Vous avez des modifications non enregistrées.</p>
          <button type="button" onClick={enregistrer} disabled={enregistrement} className="ml-auto text-xs font-semibold text-amber-800 underline">
            {enregistrement ? 'Enregistrement…' : 'Enregistrer maintenant'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ReglagesInventaires;
